import type { RowBinding, RowSource } from "./index"

/**
 * The structural shape of a graph-store `SelectCursor` that the table consumes.
 * Declared locally (graph-store does not export the `SelectCursor` type) so the
 * results-table package carries no compile-time dependency on graph-store.
 */
export interface SelectCursorLike {
  readonly vars: string[]
  readonly total: number
  getRange(
    offset: number,
    count: number,
  ): Promise<
    Array<
      Record<
        string,
        { type: string; value: string; "xml:lang"?: string; datatype?: string }
      >
    >
  >
}

/**
 * Adapt a graph-store `SelectCursor` to the table's {@link RowSource}.
 *
 * `total` is read live from the cursor — it is only the rows seen so far and
 * grows as the cursor is pulled, becoming exact once the cursor completes — so
 * the table can grow its scroll area as more rows are revealed. Pass
 * `totalOverride` when an exact count is known up front (e.g. the index's
 * all-triples view, where `graphStore.size()` is the row count) so the
 * scrollbar is correctly sized from the first paint.
 */
export function cursorRowSource(
  cursor: SelectCursorLike,
  totalOverride?: number,
): RowSource {
  return {
    get total() {
      return totalOverride ?? cursor.total
    },
    vars: cursor.vars,
    // A SparqlBinding term's `type` is the wider `string`; cursor rows are in
    // fact the concrete RDF term shapes the formatter handles, so cast across.
    getRange: (offset, count) =>
      cursor.getRange(offset, count) as Promise<RowBinding[]>,
  }
}
